import { useEffect, useState, useRef, useMemo } from 'react'
import * as fabric from 'fabric';
import Modal from 'react-bootstrap/Modal';
import { ChildModal } from '../../../views/Utility.jsx';
import { fetchPostAPI } from '../../../api/GenericFetch.js';
import { MST_LAYOUT_UPDATE_MAPPING_LAYOUT_URL } from  '../../../const/APIUrl.js';
import { FONT_LIST } from  '../../../const/List.js';
import { BlockUI, BootstrapAlertMessage } from '../../Utility.jsx';
import ButtonGroup from '@mui/material/ButtonGroup';
import { trim } from '../../../helpers/String.js';
import { SUCCESSFULLY_SAVE } from  '../../../const/Message.js';
import { setDelay } from  '../../../helpers/UI.js';
import { BaseResponseModel } from '../../../models/BaseResponseModel.tsx';
import { populateBody, drawText, renderMappingLayout, drawRackLabel, checkRackIdExists, getSelectionAssignedRacks, initialCursorPos } from '../../../services/MappingLayoutService.tsx';
import { MappingLayoutCanvas, FormRackDetail, FormAddText, GuidanceMappingLayout } from '../Layout/MappingLayoutComponents.js';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

import {
  Button,
  InputLabel
} from '@mui/material';

const delayMessage = 2000;
const disabledRackColor = '#808080';

export default function DialogMappingLayout({setShowMappingLayout, mappingLayoutData, rackTypeList, rackCategoryList})
{       
    const isPressingCtrl = useRef(false);
    const isPressingAlt = useRef(false);
    const [ showGuidance, setShowGuidance ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ showFormRackDetail, setShowFormRackDetail ] = useState(false);
    const [ showFormAddText, setShowFormAddText ] = useState(false);
    const [ rackDictByKey, setRackDictByKey ] = useState(new Map());    
    const [ fetchSubmitResponseMessages , setFetchSubmitResponseMessages ] = useState(new BaseResponseModel());
    const [ applyResponseMessages , setApplyResponseMessages ] = useState([]);
    const [ selectedRack, setSelectedRack ] = useState({});
    const [ selectionRackMap, setSelectionRackMap ] = useState(new Map());
    const selectAdditionalRef = useRef(null);
    const canvasRef = useRef(null);
    const fabricCanvasRef = useRef(null);  
    const cursorPosRef = useRef(initialCursorPos);

    const rackTypeDict = useMemo(() => {
        const rackTypeDict =  [];
        rackTypeList.map(d => {
            rackTypeDict[d.value] = d.color;
        });
        return rackTypeDict;
    });

    useEffect(() => {
        fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
            width: mappingLayoutData.LayoutData.CanvasWidth,
            height: mappingLayoutData.LayoutData.CanvasHeight,
        });

        let rackMapData = renderMappingLayout(fabricCanvasRef.current, mappingLayoutData.ObjectData, rackTypeDict);        
        setRackDictByKey(rackMapData);

        const handleWindowKeyDown = (event) => {
            isPressingCtrl.current = event.key === 'Control';
            isPressingAlt.current = event.key === 'Alt';
            
            if (event.key === 'Delete' && selectAdditionalRef.current !== null) {
                if (window.confirm('Are you sure you want to delete selected object?')) {
                    fabricCanvasRef.current.getObjects().map((e) => {
                        if (e === selectAdditionalRef.current)
                        {
                            fabricCanvasRef.current.remove(selectAdditionalRef.current);                            
                        }
                    });
                }
            }
        };

        const handleWindowKeyUp = (event) => {
            isPressingCtrl.current = false;
            isPressingAlt.current = false;            
        };
        
        window.addEventListener('keydown', handleWindowKeyDown);
        window.addEventListener('keyup', handleWindowKeyUp);

        return () => {
            window.removeEventListener('keydown', handleWindowKeyDown);
            window.removeEventListener('keyup', handleWindowKeyUp);
            window.removeEventListener('keyup', handleWindowKeyUp);

            if (fabricCanvasRef.current) {                
                fabricCanvasRef.current.dispose(); // Clean up resources
            }
        };
    }, []);    

    if (fabricCanvasRef.current)
    {
        fabricCanvasRef.current.off('mouse:down');
        fabricCanvasRef.current.off('mouse:over');
        fabricCanvasRef.current.off('mouse:up');
        
        fabricCanvasRef.current.on('mouse:down', (e) => {
            let posX = e.pointer.x;
            let posY = e.pointer.y;                        
            cursorPosRef.current.startX = posX;
            cursorPosRef.current.startY = posY;

            const selectedObj = e.target;            
            if (selectedObj === undefined) return;                    

            if (selectedObj.isAdditional)
            {
                selectAdditionalRef.current = selectedObj;             
                return;
            }

            const selectedRackObject = rackDictByKey.get(selectedObj.key);
            selectedRackObject.RackTypeId = selectedRackObject.RackTypeId ?? rackTypeList[0].value; //set default value of racktypeid
            setSelectedRack(selectedRackObject);
            setApplyResponseMessages([]);
            setShowFormRackDetail(true);
        });

        fabricCanvasRef.current.on('mouse:over', (e) => {
        if ((isPressingCtrl.current || isPressingAlt.current) && e !== undefined && e.target !== undefined && e.target.isRack !== undefined && e.target.isRack && e.target.RackId === '')
        {
            let isDisabled = (isPressingCtrl.current && !isPressingAlt.current); 
            e.target.set("isDisabled", isDisabled);
            e.target.set("fill",  isDisabled? disabledRackColor : "#FFF");
            fabricCanvasRef.current.requestRenderAll();
        }
        });

        fabricCanvasRef.current.on('mouse:up', (e) => {            
            let posX = e.pointer.x;
            let posY = e.pointer.y;            
            cursorPosRef.current.endX = posX;
            cursorPosRef.current.endY = posY;            
            let getSelectionRackMap = getSelectionAssignedRacks({canvas : fabricCanvasRef.current, cursorPos : cursorPosRef.current});
            setSelectionRackMap(getSelectionRackMap);
        });
    }    

    const handleEmptySelectionRacks = () => {
        let updatedRackList = new Map(rackDictByKey);
        fabricCanvasRef.current.getObjects().map((d) => {
            if (selectionRackMap.has(d.key))
            {                
                if (d.type === 'text') {
                    d.set('text', '');
                }
                else
                {
                    d.set("fill", '#FFF');
                    d.set('strokeWidth', 1);
                    d.set('stroke', '#000');
                }
             
                d.set('RackId', '');
                d.set('RackName', '');
                d.set('Notes', '');
                d.set('RackTypeId', '');
                d.set('RackCategoryListId', []);
                        
                let rack = updatedRackList.get(d.key);
                rack.RackId = '';
                rack.RackName = '';
                rack.Notes = '';
                rack.RackTypeId = '';
                rack.RackCategoryListId = [];
                updatedRackList[d.key] = rack;
            }
        });
        
        setRackDictByKey(updatedRackList);
        fabricCanvasRef.current.requestRenderAll();
    }

    const handleChildModalClose = () => {
        if (showFormRackDetail) setShowFormRackDetail(false);
        if (showFormAddText) setShowFormAddText(false);
    };

    const handleApplyAddText = (inputData) => {
        drawText(fabricCanvasRef.current, inputData);
        fabricCanvasRef.current.requestRenderAll(); 
    };

    const handleUpdateMappingLayout = async () => {
        setIsLoading(true);
        setFetchSubmitResponseMessages(new BaseResponseModel());

        let body = populateBody(mappingLayoutData.LayoutData.Id, fabricCanvasRef.current);        

        const response = await fetchPostAPI({url : MST_LAYOUT_UPDATE_MAPPING_LAYOUT_URL, body, includeBundleResponse: true});
        let responseData = await response.json();

        if (!response.ok || !responseData.isSuccess)
        {
            setFetchSubmitResponseMessages(prevValue => {
                let newValue = { ...prevValue };
                newValue.variant = 'danger';
                newValue.messages = responseData.errorMessages;
                return newValue;
            });

            setDelay(delayMessage).then(() => {
                setFetchSubmitResponseMessages(new BaseResponseModel());
            });
        }
        
        if (response.ok && responseData.isSuccess)
        {
             setFetchSubmitResponseMessages(prevValue => {
                let newValue = { ...prevValue };
                newValue.variant = 'success';
                newValue.messages = [SUCCESSFULLY_SAVE];
                return newValue;
            });
            setDelay(delayMessage).then(() => {
                setFetchSubmitResponseMessages(new BaseResponseModel());
            });
        }

        setIsLoading(false);
    };

    const handleRackDetailApply = ()=> {
        let messages = [];
        if (trim(selectedRack.RackId) === '')
        {
            messages.push('Rack No can`t be empty');
        }
        else if (checkRackIdExists(rackDictByKey, selectedRack))
        {
            messages.push('Rack No already exists');
        }

        if (selectedRack.RackCategoryListId.length === 0 || (selectedRack.RackCategoryListId.length === 1 && selectedRack.RackCategoryListId[0] === ''))
        {
            messages.push('Select Rack Category');
        }
        
        if (messages.length > 0) {
            setApplyResponseMessages(messages);
            return;
        }
        
        let updatedRackList = new Map(rackDictByKey);
        updatedRackList.set(selectedRack.key, selectedRack);
        setRackDictByKey(updatedRackList);       
        setShowFormRackDetail(false);
        fabricCanvasRef.current.getObjects().map((d) => {
            if (d.key == selectedRack.key)
            {                
                let isDrawText = d.RackId === null || d.RackId === '';
                let color = rackTypeDict[selectedRack.RackTypeId];
                if (d.type === 'text') {
                    d.set('text', selectedRack.RackId);
                }
                else
                {
                    d.set('fill', color);
                }
                
                d.set('RackId', selectedRack.RackId);
                d.set('RackName', selectedRack.RackName);
                d.set('Notes', selectedRack.Notes);
                d.set('RackTypeId', selectedRack.RackTypeId);
                d.set('RackCategoryListId', selectedRack.RackCategoryListId);

                if (isDrawText) 
                {
                   d.set('stroke', 0);
                }
            }
        });
        fabricCanvasRef.current.requestRenderAll();
    };

    const handleOpenFormAddText = () => {
        setShowFormAddText(true);
    };   

    return(
        <>    
            <Modal show={true} fullscreen={true} onHide={() => setShowMappingLayout(false)} keyboard={false}>
                <Modal.Header closeButton={true} className="py-1">
                <Modal.Title>
                    {mappingLayoutData.LayoutData.LayoutName}
                    {fetchSubmitResponseMessages.messages.length > 0 && <BootstrapAlertMessage type={"error"} variant={fetchSubmitResponseMessages.variant} messages={fetchSubmitResponseMessages.messages} />}
                </Modal.Title>
                </Modal.Header>
                {            
                    showFormRackDetail &&
                    <ChildModal handleClose={handleChildModalClose} title={"Rack Detail"}>
                        <FormRackDetail handleApply={handleRackDetailApply} handleClose={handleChildModalClose} applyResponseMessages={applyResponseMessages}
                            selectedRack={selectedRack} setSelectedRack={setSelectedRack} rackTypeList={rackTypeList} rackCategoryList={rackCategoryList} />
                    </ChildModal>
                }
                {
                    showFormAddText &&
                    <ChildModal handleClose={handleChildModalClose} title={"Add Text"}>
                        <FormAddText fontList={FONT_LIST} handleApplyAddText={handleApplyAddText} handleClose={handleChildModalClose}/>
                    </ChildModal>
                }
                <Modal.Body>                    
                    { isLoading && <BlockUI /> }
                    { showGuidance && <ChildModal title={"Guidance"} handleClose={() => setShowGuidance(false) } width="700px"><GuidanceMappingLayout /></ChildModal>}
                    <div className="row mb-3">
                        <ButtonGroup variant="outlined" className="" aria-label="Basic button group">
                            <Button onClick={() => setShowGuidance(!showGuidance) }><InfoOutlineIcon /></Button>
                            <Button type="button" className="" onClick={handleUpdateMappingLayout}>Save</Button>
                            <Button className="" onClick={handleOpenFormAddText}>Add Text</Button>
                            <Button className="" onClick={handleEmptySelectionRacks}>Empty selection ({selectionRackMap.size}) rack(s)</Button>
                            <Button>Refresh after disabled / enabled rack</Button>                           
                        </ButtonGroup>
                    </div>
                    <MappingLayoutCanvas canvasRef={canvasRef} layoutId={mappingLayoutData.LayoutData.Id} />
                </Modal.Body>
            </Modal>
        </>
    );    
}